import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
} from 'react';

import { FeatureToggles, featureToggles } from './featureToggles';

type ToggleContext = FeatureToggles & {
  setToggles?: Dispatch<SetStateAction<FeatureToggles>>;
};

export const FeatureToggleContext = createContext<ToggleContext>(featureToggles);

type Feature = keyof typeof featureToggles;

export const useFeatureToggle = (feature: Feature): boolean => {
  const toggleContext = useContext(FeatureToggleContext);
  return toggleContext[feature];
};

export const useResetFeatureToggles = () => {
  const toggleContext = useContext(FeatureToggleContext);
  return () => {
    if (toggleContext.setToggles) toggleContext.setToggles(featureToggles);
  };
};

export const useAllFeatureToggles = (): FeatureToggles => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setToggles, ...toggles } = useContext(FeatureToggleContext);
  return toggles;
};

export const useSetFeatureToggle = () => {
  const toggleContext = useContext(FeatureToggleContext);
  return {
    setToggle: (feature: Feature, value: boolean) => {
      if (toggleContext.setToggles)
        return toggleContext.setToggles((oldToggles) => ({ ...oldToggles, [feature]: value }));
    },
    flipToggle: (feature: Feature) => {
      if (toggleContext.setToggles)
        return toggleContext.setToggles((oldToggles) => ({
          ...oldToggles,
          [feature]: !oldToggles[feature],
        }));
    },
  };
};

export const Enable = ({
  when: toggleName,
  children,
}: PropsWithChildren<{ when: keyof FeatureToggles; on: true }>) => {
  const isEnabled = useFeatureToggle(toggleName);
  return <>{isEnabled && children}</>;
};

export const getInitialState = (): FeatureToggles => {
  // if (!['local', 'dev'].includes(environment)) return featureToggles;
  // Technically the initializer of useState should be pure, but its only local
  // storage...
  const storedTogglesJson = localStorage.getItem('featureToggles');
  const storedToggles = storedTogglesJson ? JSON.parse(storedTogglesJson) : {};

  const filteredToggles = Object.fromEntries(
    Object.entries(storedToggles).filter(([key]) => Object.keys(featureToggles).includes(key))
  );

  return { ...featureToggles, ...filteredToggles };
};
